import PropTypes from 'prop-types';
import { Dialog, DialogContent } from '../../../ui/Dialog';
import { IconClose } from '../../../Components/Common/Icons';
import { useLanguage } from '../../../localization/useLanguage';
import styles from './MuteDurationDialog.module.scss';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const DURATIONS = [
    { key: 'MUTE_FOR_1_HOUR', value: HOUR_MS },
    { key: 'MUTE_FOR_8_HOURS', value: 8 * HOUR_MS },
    { key: 'MUTE_FOR_1_DAY', value: DAY_MS },
    { key: 'MUTE_FOR_1_WEEK', value: 7 * DAY_MS },
    { key: 'MUTE_UNTIL_CHANGED', value: null },
];

/**
 * Asks how long a conversation should stay muted.
 *
 * Picking an option mutes straight away — there is no confirm step, which is why the
 * options are buttons rather than a radio group.
 *
 * @param {object} props Open state, the conversation name, and the pick handler.
 * @returns {React.ReactElement} The dialog.
 */
const MuteDurationDialog = ({ open, chatName = '', onOpenChange, onPick }) => {
    const { t } = useLanguage();

    const handlePick = value => {
        onOpenChange(false);
        onPick(value);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={styles.dialog}>
                <div className={styles.head}>
                    <h3>{t('chat.menu.MUTE_CHAT')}</h3>
                    <button
                        type="button"
                        className={styles.close}
                        onClick={() => onOpenChange(false)}
                        aria-label={t('common.CLOSE')}
                    >
                        <IconClose />
                    </button>
                </div>

                <p className={styles.prompt}>{t('chat.menu.MUTE_DURATION_PROMPT', { name: chatName })}</p>

                <div className={styles.options}>
                    {DURATIONS.map(({ key, value }) => (
                        <button key={key} type="button" className={styles.option} onClick={() => handlePick(value)}>
                            {t(`chat.menu.${key}`)}
                        </button>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
};

MuteDurationDialog.propTypes = {
    open: PropTypes.bool,
    chatName: PropTypes.string,
    onOpenChange: PropTypes.func.isRequired,
    onPick: PropTypes.func.isRequired,
};

export default MuteDurationDialog;
